import React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, FormGroup } from '../ui';

const SortingControls = ({
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  filteredProps,
  currentPage,
  setCurrentPage,
  itemsPerPage
}) => {
  const getSortingOptions = () => {
    return [
      { value: 'distance', label: 'Distance', icon: '📍' },
      { value: 'name', label: 'Name A-Z', icon: '🔤' },
      { value: 'category', label: 'Category', icon: '📁' },
      { value: 'availability', label: 'Availability', icon: '🟢' },
      { value: 'owner', label: 'Organization', icon: '🏢' },
      { value: 'recently_added', label: 'Recently Added', icon: '🆕' },
      { value: 'popularity', label: 'Most Popular', icon: '🔥' }
    ];
  };

  // MUI-Style Pagination Component
  const MUIPagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
      <div className="flex items-center justify-between text-sm text-gray-700">
        {/* Results Info */}
        <div>
          Showing {startItem}-{endItem} of {totalItems} results
        </div>
        
        {/* Page Controls */}
        <div className="flex items-center gap-2">
          <span>Page:</span>
          <Button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            variant="secondary"
            size="compact"
            icon={ChevronLeft}
            className="p-1"
          />
          <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded font-medium">
            {currentPage}
          </span>
          <span>of {Math.ceil(totalItems / itemsPerPage)}</span>
          <Button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
            variant="secondary"
            size="compact"
            icon={ChevronRight}
            className="p-1"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex flex-col gap-4">
        {/* Top Row: Results + Sorting */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* Results Summary */}
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-gray-900">
              Found {filteredProps.length} props
            </div>
            {sortBy !== 'distance' && (
              <div className="hidden sm:flex items-center gap-1 text-xs text-gray-500">
                <span>•</span>
                <span>Sorted by {getSortingOptions().find(opt => opt.value === sortBy)?.label.toLowerCase()}</span>
                <span className="text-gray-400">({sortOrder === 'asc' ? '↑' : '↓'})</span>
              </div>
            )}
          </div>
          
          {/* Modern Sorting Interface */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Sort Method Selector */}
            <div className="relative flex-1 sm:flex-none">
              <FormGroup.Select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-40 pr-8 text-sm font-medium text-gray-700"
              >
                {getSortingOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FormGroup.Select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
            
            {/* Sort Direction Toggle */}
            <Button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              variant="secondary"
              size="compact"
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              className={sortOrder === 'asc' 
                ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' 
                : ''}
            >
              <span className="text-sm font-medium">
                {sortOrder === 'asc' ? '↑' : '↓'}
              </span>
              <span className="hidden sm:inline text-xs ml-1">
                {sortOrder === 'asc' ? 'Asc' : 'Desc'}
              </span>
            </Button>
            
            {/* Quick Sort Pills - Only on larger screens */}
            <div className="hidden lg:flex items-center gap-1 ml-1 pl-3 border-l border-gray-200">
              <Button
                onClick={() => { setSortBy('distance'); setSortOrder('asc'); }}
                variant="secondary"
                size="compact"
                className={`rounded-full text-xs font-medium ${
                  sortBy === 'distance' 
                    ? 'bg-blue-100 text-blue-700 border-blue-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-200'
                }`}
              >
                <span>📍</span>
                <span>Nearest</span>
              </Button>
              <Button
                onClick={() => { setSortBy('availability'); setSortOrder('asc'); }}
                variant="secondary"
                size="compact"
                className={`rounded-full text-xs font-medium ${
                  sortBy === 'availability' 
                    ? 'bg-green-100 text-green-700 border-green-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-200'
                }`}
              >
                <span>🟢</span>
                <span>Available</span>
              </Button>
              <Button
                onClick={() => { setSortBy('recently_added'); setSortOrder('desc'); }}
                variant="secondary"
                size="compact"
                className={`rounded-full text-xs font-medium ${
                  sortBy === 'recently_added' 
                    ? 'bg-purple-100 text-purple-700 border-purple-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-200'
                }`}
              >
                <span>🆕</span>
                <span>Newest</span>
              </Button>
            </div>
          </div>
        </div>
        
        {/* Bottom Row: Pagination */}
        <MUIPagination 
          totalItems={filteredProps.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default SortingControls;